/**
 * Banner click / impression tracking.
 *
 * Pushes GTM dataLayer events for every banner on the storefront (home
 * slideshow, collection page header, promotion banners, image cards).
 *
 * Events pushed to the GTM dataLayer:
 *   banner_click     - custom event, flat payload, easy to build GTM triggers on
 *   select_promotion - GA4 recommended event (ecommerce object)
 *   banner_view      - custom impression event (fired once per banner per page view)
 *   view_promotion   - GA4 recommended impression event
 *
 * In addition, when themeBannerTracking.sendToGa4 is true the same events are
 * sent straight to GA4 through the gtag.js already on the page, so the reports
 * work with no Google Tag Manager setup:
 *   banner_click / banner_view         - flat banner_* params. Register them as
 *                                        custom dimensions in GA4 to break
 *                                        clicks down per banner / page.
 *   select_promotion / view_promotion  - carry an items[] entry per banner so
 *                                        GA4's built-in Monetization >
 *                                        Promotions report fills in by itself.
 *
 * Markup contract: any element carrying [data-banner-track] is a banner.
 * Optional attributes on that element:
 *   data-banner-id, data-banner-name, data-banner-position,
 *   data-banner-creative, data-banner-location, data-banner-section
 */
(function () {
  'use strict';

  var config = window.themeBannerTracking || {};
  var BANNER_SELECTOR = '[data-banner-track]';
  // What counts as "the banner was clicked": a link, an add-to-cart submit
  // button (slides pointing at a /products/ URL render one instead of a link),
  // or anything explicitly opted in.
  var TRIGGER_SELECTOR = 'a[href], button[name="add"], [data-banner-cta]';
  // Fallbacks for banners that were not (yet) annotated in Liquid.
  var FALLBACK_SELECTOR = '.m-slide, .m-image-card, .m-collection-page-header, .m-banner-promotion';
  // Links that live inside a banner but are not the banner itself.
  var IGNORE_SELECTOR = '[data-banner-ignore], .m-breadcrumb, .m-slider-controls, nav';
  var seenImpressions = {};

  function dataLayer() {
    window.dataLayer = window.dataLayer || [];
    return window.dataLayer;
  }

  function text(el) {
    if (!el) return '';
    return (el.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 120);
  }

  function fileName(url) {
    if (!url) return '';
    return url.split('?')[0].split('/').pop();
  }

  function inferCreative(banner) {
    var img = banner.querySelector('img');
    if (!img) return '';
    return fileName(img.getAttribute('src') || img.getAttribute('data-src') || '');
  }

  function inferName(banner) {
    var heading = banner.querySelector(
      '.m-slide__title, .m-image-card__heading, .m-richtext__title, h1, h2, h3'
    );
    if (heading && text(heading)) return text(heading);
    var img = banner.querySelector('img[alt]');
    if (img && img.getAttribute('alt')) return img.getAttribute('alt').trim();
    return inferCreative(banner) || 'banner';
  }

  function position(banner) {
    if (banner.hasAttribute('data-banner-position')) {
      return banner.getAttribute('data-banner-position');
    }
    var slide = banner.closest ? banner.closest('[data-slide]') : null;
    if (slide) return String(parseInt(slide.getAttribute('data-slide'), 10) + 1);
    var all = Array.prototype.slice.call(
      document.querySelectorAll(BANNER_SELECTOR + ', ' + FALLBACK_SELECTOR)
    );
    return String(all.indexOf(banner) + 1);
  }

  function sectionId(banner) {
    var section = banner.closest ? banner.closest('[data-section-id]') : null;
    return section ? section.getAttribute('data-section-id') : '';
  }

  function sectionType(banner) {
    if (banner.getAttribute('data-banner-section')) {
      return banner.getAttribute('data-banner-section');
    }
    var section = banner.closest ? banner.closest('[data-section-type]') : null;
    return section ? section.getAttribute('data-section-type') : '';
  }

  function describe(banner) {
    var name = banner.getAttribute('data-banner-name') || inferName(banner);
    var id = banner.getAttribute('data-banner-id') || sectionId(banner) || name;
    return {
      banner_id: id,
      banner_name: name,
      banner_position: position(banner),
      banner_creative: banner.getAttribute('data-banner-creative') || inferCreative(banner),
      banner_section: sectionType(banner),
      banner_location: banner.getAttribute('data-banner-location') || config.pageType || '',
      page_type: config.pageType || '',
      page_template: config.template || '',
      page_handle: config.handle || '',
      page_path: window.location.pathname
    };
  }

  function promotion(payload) {
    return {
      creative_name: payload.banner_creative || payload.banner_name,
      // e.g. home_slideshow_2, collection_collection-header_1
      creative_slot: [payload.banner_location, payload.banner_section, payload.banner_position]
        .filter(Boolean)
        .join('_'),
      promotion_id: payload.banner_id,
      promotion_name: payload.banner_name,
      location_id: payload.banner_location
    };
  }

  // GA4 attaches promotion data to items, so an event without items never
  // reaches the Promotions report. Represent the banner itself as the item.
  function promotionItems(payload) {
    return [
      Object.assign(
        {
          item_id: payload.banner_id,
          item_name: payload.banner_name,
          index: parseInt(payload.banner_position, 10) || 1,
          quantity: 1
        },
        promotion(payload)
      )
    ];
  }

  function bannerParams(payload) {
    return {
      banner_id: payload.banner_id,
      banner_name: payload.banner_name,
      banner_position: payload.banner_position,
      banner_location: payload.banner_location,
      banner_section: payload.banner_section,
      banner_creative: payload.banner_creative,
      banner_url: payload.banner_url || '',
      banner_cta: payload.banner_cta || '',
      page_handle: payload.page_handle || '',
      page_path: payload.page_path || ''
    };
  }

  function sendToGa4(eventName, ga4EventName, payload) {
    if (!config.sendToGa4 || !config.ga4Id) return;
    if (typeof window.gtag !== 'function') return;

    var params = bannerParams(payload);

    // Plain custom event: shows up in GA4 as "banner_click" / "banner_view"
    // and every banner_* param can be registered as a custom dimension.
    window.gtag('event', eventName, Object.assign({ send_to: config.ga4Id }, params));

    // GA4 recommended promotion event: feeds Monetization > Promotions.
    window.gtag(
      'event',
      ga4EventName,
      Object.assign({ send_to: config.ga4Id }, promotion(payload), params, {
        items: promotionItems(payload)
      })
    );
  }

  function pushEvents(eventName, ga4EventName, payload) {
    var dl = dataLayer();
    dl.push(Object.assign({ event: eventName }, payload));
    // GA4 recommended promotion event shape, for anyone building GTM tags.
    dl.push({ ecommerce: null });
    dl.push({
      event: ga4EventName,
      ecommerce: Object.assign(promotion(payload), { items: promotionItems(payload) })
    });

    sendToGa4(eventName, ga4EventName, payload);
  }

  function onClick(event) {
    if (event.type === 'auxclick' && event.button !== 1) return;

    var link = event.target.closest ? event.target.closest(TRIGGER_SELECTOR) : null;
    if (!link) return;

    if (link.closest(IGNORE_SELECTOR)) return;

    var banner = link.closest(BANNER_SELECTOR) || link.closest(FALLBACK_SELECTOR);
    if (!banner) return;

    var href = link.tagName === 'A' ? link.getAttribute('href') : null;
    var label = text(link) || link.getAttribute('aria-label') || '';

    var payload = describe(banner);
    // An add-to-cart slide has no href of its own, so fall back to the
    // destination recorded on the banner.
    payload.banner_url = href || banner.getAttribute('data-banner-url') || '';
    if (href) {
      payload.banner_cta = label || 'image';
      payload.banner_cta_type = text(link) ? 'button' : 'image';
    } else {
      payload.banner_cta = label || 'add to cart';
      payload.banner_cta_type = 'add_to_cart';
    }

    pushEvents('banner_click', 'select_promotion', payload);
  }

  function trackImpressions() {
    if (!('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.5) return;
          var banner = entry.target;
          var payload = describe(banner);
          var key = payload.banner_id + '|' + payload.banner_position;
          if (seenImpressions[key]) {
            observer.unobserve(banner);
            return;
          }
          seenImpressions[key] = true;
          pushEvents('banner_view', 'view_promotion', payload);
          observer.unobserve(banner);
        });
      },
      { threshold: [0.5] }
    );

    document.querySelectorAll(BANNER_SELECTOR).forEach(function (banner) {
      observer.observe(banner);
    });
  }

  function init() {
    // Never pollute analytics from the theme editor.
    if (window.Shopify && window.Shopify.designMode) return;

    document.addEventListener('click', onClick, true);
    document.addEventListener('auxclick', onClick, true);
    trackImpressions();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Sections re-rendered by the Shopify section API (filters, pagination).
  document.addEventListener('shopify:section:load', trackImpressions);
})();
